<template>
  <h5 class="card-title mb-2 fw-bold fs-3">Loyalty Program Setup</h5>
  <div class="row">
    <div class="col">
      <p style="color: #64748b; font-size: 16px">
        How do members earn {{ isLoyaltyPoints ? "points" : "stamps" }}?
        <a
          @click="store.goTo('PointSetup')"
          class="edit-btn"
          style="color: #ff0000; cursor: pointer"
          >Edit</a
        >
      </p>
      <ul class="points-list" style="font-size: 16px">
        <li v-if="store.brandEarningSelection === 'dynamic'">
          Members earn {{ store.brandPoints }}
          {{ isLoyaltyPoints ? "point(s)" : "stamp(s)" }} for every
          {{
            currencyConvertWCountry(brandSpending, true, store.brandCurrency)
          }}
          they spend
          <i class="ms-1 bi bi-exclamation-circle" style="color: #ff0000"></i>
        </li>
        <li v-else-if="store.brandEarningSelection === 'fixed'">
          Members earn {{ store.brandPoints }}
          {{ isLoyaltyPoints ? "point(s)" : "stamp(s)" }} when they spend at
          least
          {{
            currencyConvertWCountry(brandSpending, true, store.brandCurrency)
          }}
          in 1 visit
          <i class="ms-1 bi bi-exclamation-circle" style="color: #ff0000"></i>
        </li>
      </ul>
    </div>
  </div>
  <div class="row">
    <div class="col">
      <p style="color: #64748b; font-size: 16px; margin-top: 10px" class="pb-2">
        Choose Rewards Members can redeem with
        {{ !isLoyaltyPoints ? "a complete" : "" }}
        {{ isLoyaltyPoints ? "points" : "stamp card" }}
      </p>
      <p v-if="isLoyaltyPoints" style="font-size: 0.8rem" class="pb-2">
        *Only up to 3 will appear on the poster
      </p>
    </div>
  </div>
  <template v-if="isLoyaltyPoints">
    <div class="row row-cols-2 g-2 mt-1" v-if="store.rewards.length > 0">
      <div class="col" v-for="(reward, index) in store.rewards" :key="index">
        <div class="d-flex card" :class="{ 'card-selected': reward.active }">
          <label :for="`custom-reward-${index}-checkbox`" class="w-100">
            <div class="card-body d-flex align-items-center">
              <input
                class="form-check-input me-2"
                v-model="reward.active"
                type="checkbox"
                :class="{ checked: reward.active }"
                :id="`custom-reward-${index}-checkbox`"
              />
              <i
                :class="getRewardIcon(reward.promoType)"
                class="me-2"
                style="width: 23px; height: 23px; font-size: 14px"
              ></i>
              <span class="deals-text">{{ reward.title }}</span>
              <span class="points-text ms-auto"
                >{{ reward.points }} Points</span
              >
            </div>
          </label>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="row row-cols-2 g-2 mt-1" v-if="store.rewards.length > 0">
      <div class="col" v-for="(reward, index) in store.rewards" :key="index">
        <div class="d-flex card" :class="{ 'card-selected': reward.active }">
          <label :for="`custom-reward-${index}-checkbox`" class="w-100">
            <div class="card-body d-flex align-items-center">
              <input
                type="checkbox"
                class="form-check-input me-2"
                :class="{ checked: reward.active }"
                @change="selectReward(index)"
                :checked="reward.active"
                :id="`custom-reward-${index}-checkbox`"
                name="reward-checkbox"
              />
              <i
                :class="getRewardIcon(reward.promoType)"
                class="me-2"
                style="width: 23px; height: 23px; font-size: 14px"
              ></i>
              <span class="deals-text">{{ reward.title }}</span>
            </div>
          </label>
        </div>
      </div>
    </div>
  </template>

  <div class="row mt-3">
    <div class="col">
      <button class="btn btn-action py-2 px-3" @click="handleAddReward">
        <span>+ Add Reward</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { useBrandSetupStore, useOnboardingModalsStore } from "@/stores/brand";
import { useMutation, useQuery } from "@urql/vue";
import {
  CREATE_BRAND_AND_OUTLETS,
  INDUSTRY_CONFIGURATION,
} from "@/constants/graphql";
import { computed, ref, nextTick, watch } from "vue";
import { currencyFormat } from "@/pages/LoyaltyProgram/RedeemingPoints/Reusables/functions.js";
import { Modal } from "bootstrap";
import {
  currencyConvertWCountry,
  formatBillingCurrency,
  formatCurrency,
} from "@/constants/country_currency";
import { storeToRefs } from "pinia";

const emit = defineEmits(["navigate"]);

const store = useBrandSetupStore();

const industryConfigQuery = useQuery({
  query: INDUSTRY_CONFIGURATION,
  variables: { industryId: store.brandIndustry.id },
});
const { executeMutation: createBrandAndOutletsMutation } = useMutation(
  CREATE_BRAND_AND_OUTLETS
);

const modalsStore = useOnboardingModalsStore();

const loading = ref(false);

const brandSpending = computed(() => {
  return currencyFormat(store.brandSpending);
});
const isLoyaltyPoints = computed(() => {
  return store.loyaltyProgramType === "points";
});

function handleAddReward() {
  if (isLoyaltyPoints.value) {
    const modal = new Modal(document.getElementById("addRewardModal"));
    modal.show();
  } else {
    const modal = new Modal(document.getElementById("addRewardStampCardModal"));
    modal.show();
  }
}

const selectReward = (index) => {
  store.rewards.forEach((reward, i) => {
    reward.active = i === index;
  });
};

function getRewardIcon(type) {
  switch (type) {
    case "free_item":
      return "bi bi-gift-fill";
    case "cash_discount":
      return "bi bi-coin";
    case "percentage_discount":
      return "bi bi-percent";
    case "free_shipping":
      return "bi bi-truck";
    default:
      return "bi bi-gift";
  }
}

watch(industryConfigQuery.fetching, (fetching) => {
  if (!fetching && industryConfigQuery.data.value?.industryConfiguration) {
    const config = industryConfigQuery.data.value.industryConfiguration;

    store.$patch({
      valueOfRedemption: [
        config.t1ValueOfRedemption,
        config.t2ValueOfRedemption,
        config.t3ValueOfRedemption,
      ],
      noOfVisits: [
        config.t1NoOfVisits,
        config.t2NoOfVisits,
        config.t3NoOfVisits,
      ],
    });

    if (isLoyaltyPoints.value) {
      store.$patch({
        rewards: store.defaultReward,
      });
    } else {
      store.$patch({
        rewards: store.defaultStampReward,
      });
    }
  }
});

async function handleCreateProgram() {
  if (loading.value) return;
  loading.value = true;

  await nextTick();

  const rewardData = rewardList.value
    .filter((reward) => reward.active)
    .map((reward) => ({
      promoTitle: reward.title,
      promoType: reward.promoType,
      promoCost: reward.points,
      promoMonetaryCost: Math.floor(reward.rewardValue ?? 0),
    }));

  const outlet = store.outlets.map((val) => ({
    location: val.outletLocation,
    timezone: parseInt(val.outletTimezone),
  }));

  store.rewards = store.rewards.map((reward) => ({
    ...reward,
    promoMonetaryCost: reward.promoMonetaryCost ?? 0,
  }));

  try {
    const result = await createBrandAndOutletsMutation({
      name: store.brandName,
      industry: store.brandIndustry,
      countryCode: store.brandCountry,
      outlets: outlet,
      stpcThreshold: parseInt(store.brandSpending),
      stpcModifier: parseInt(store.brandPoints),
      stpcSpecification: store.brandEarningSelection,
      promotions: rewardData,
      timezone: store.brandTimeZone.zone,
      collectionType: "points",
    });

    if (result.error) {
      console.error("GraphQL request error:", result.error);
      alert(
        `Failed to create program: ${result.error.message || "Unknown error"}`
      );
    } else if (
      result.data &&
      result.data.createBrandAndOutlets.errors &&
      result.data.createBrandAndOutlets.errors.length > 0
    ) {
      const errorMessages = result.data.createBrandAndOutlets.errors
        .map((err) => err.message)
        .join("\n");
      alert(
        `An error occurred when creating the brand and outlets:\n${errorMessages}`
      );
    } else if (result.data && result.data.createBrandAndOutlets.result) {
      store.$reset();
      store.$patch({
        newBrandId: result.data.createBrandAndOutlets.brandId,
        page: "account-creds",
      });
      result.data.createBrandAndOutlets.outletCredentials.forEach(
        (credential) => {
          store.brandOutletCredentials.push({
            storeName: credential.name,
            id: credential.id,
            password: credential.password,
          });
        }
      );
    } else {
      alert("An unexpected issue occurred during program creation.");
    }
  } catch (e) {
    console.error("Mutation failed completely:", e);
    alert(
      `An unexpected error occurred: ${
        e.message || "Please check console for details."
      }`
    );
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.card-selected {
  border-color: black;
  border-width: 2px;
}
.form-check-input {
  border-radius: 50% !important;
  width: 20px;
  height: 20px;
  border: 2px solid #b7b7b7;
}
.checked {
  background-color: #ff0000;
  border-color: #ff0000;
}
.deals-text {
  font-size: 14px;
  color: #000;
}
.points-text {
  font-size: 14px;
  color: #64748b;
}
.card {
  border-radius: 8px;
  border: 1px solid #e3e8ef;
}
.btn-action {
  background-color: white;
  border-color: #ff0000;
  color: #ff0000;
}
.btn-action:hover {
  background-color: #ff0000;
  border-color: white;
  color: white;
}
.btn-arrow {
  background-color: white;
  border-color: #e3e8ef;
  color: gray;
  font-size: calc(0.5rem + 0.9vw);
}
.btn-arrow:hover {
  background-color: gray;
  border-color: gray;
  color: white;
}
.btn-customs {
  background-color: #ff0000;
  border-color: #ff0000;
  color: white;
  font-size: 14px;
}
.btn-customs:disabled {
  background-color: #cccccc;
  border-color: #cccccc;
  cursor: not-allowed;
}
.card:hover,
.card:hover label,
.form-check-input:hover {
  cursor: pointer;
}
p {
  margin-bottom: 10px !important;
}
.NewBrandNavigation {
  margin-left: -40px;
}
</style>
